import React, { useEffect, useState, useRef } from "react";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Layers, GripVertical, ImagePlus, X, Wand2, Mic, Play, Upload, Download, FileSpreadsheet, FileText, Volume2, Square } from "lucide-react";

const blank = () => ({
  id: null,
  title: "",
  bullets: "",
  duration: 4,
  motion: "still",
  narrate: false,
  voiceover: "",
  position: 9999,
  background_media_id: null,
  background_kind: null,
  bg_start: 0,
  bg_mute: true,
});

export default function InfoSlidesPanel({ project, onRegisterSave, onChange }) {
  const [slides, setSlides] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bgBusy, setBgBusy] = useState(-1);
  const [aiSource, setAiSource] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiCount, setAiCount] = useState(5);
  const [csvBusy, setCsvBusy] = useState(false);
  const [narrateBusy, setNarrateBusy] = useState(-1);
  const [narrateAllBusy, setNarrateAllBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [bgAiBusy, setBgAiBusy] = useState(-1);
  const isCards = project.kind === "cards";
  // ---- per-reel branding (cards only) ----
  const [brandColor, setBrandColor] = useState(project.settings?.card_bg || "#0A0A0C");
  const [introEnabled, setIntroEnabled] = useState(!!project.intro?.enabled);
  const [companyName, setCompanyName] = useState(project.intro?.company_name || "");
  const [companyDetails, setCompanyDetails] = useState(project.intro?.details || "");
  const [logoMediaId, setLogoMediaId] = useState(project.intro?.logo_media_id || null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  // ---- narration voice (cards only) ----
  const [voices, setVoices] = useState([]);
  const [voiceName, setVoiceName] = useState(project.settings?.voice_name || "");
  const [previewingVoice, setPreviewingVoice] = useState(false);
  const [savingVoice, setSavingVoice] = useState(false);
  const [voiceSpeed, setVoiceSpeed] = useState(project.settings?.voice_speed || 1);
  const [listenBusy, setListenBusy] = useState(-1);
  const [playAll, setPlayAll] = useState(false);
  const audioRef = useRef(null);
  const playAllRef = useRef(false);
  const slidesRef = useRef(slides);
  useEffect(() => { slidesRef.current = slides; }, [slides]);
  useEffect(() => () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } }, []);

  useEffect(() => {
    const cap = (s) => {
      const t = (s.caption || s.title || s.narration || s.text || "").toString().replace(/\s+/g, " ").trim();
      return t ? (t.length > 34 ? t.slice(0, 34) + "…" : t) : "";
    };
    const useAgent = project.settings?.use_agent !== false;
    Promise.all([
      api.get(`/projects/${project.id}/slides`).then((r) => r.data.slides || []).catch(() => []),
      api.get(`/projects/${project.id}/generation`).then((r) => r.data?.scenes || []).catch(() => []),
      api.get(`/projects/${project.id}/transcript`).then((r) => r.data?.scenes || []).catch(() => []),
    ]).then(([sl, gen, tr]) => {
      let list = [];
      if (gen && gen.length) {
        // generation scenes already include the agent intro (type: agent_intro)
        list = gen.map((s) => (s.type === "agent_intro"
          ? { kind: "agent", caption: cap(s) }
          : { kind: "scene", caption: cap(s) }));
      } else {
        // pre-generation: build from the script scenes, and prepend the agent intro
        // when an avatar is used (the render sequence puts it first)
        if (useAgent) list.push({ kind: "agent", caption: "" });
        (tr || []).forEach((s) => list.push({ kind: "scene", caption: cap(s) }));
      }
      setScenes(list);
      setSlides(sl.map((s) => ({ ...s, bullets: (s.bullets || []).join("\n") })));
      setLoading(false);
    });
  }, [project.id]);

  const update = (i, patch) => setSlides((s) => s.map((sl, idx) => (idx === i ? { ...sl, ...patch } : sl)));
  const add = () => setSlides((s) => [...s, blank()]);
  const remove = (i) => setSlides((s) => s.filter((_, idx) => idx !== i));

  const reorder = (from, to) => {
    if (from === to || from == null || to == null) return;
    setSlides((s) => {
      const arr = [...s];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return arr;
    });
  };

  const generate = async () => {
    if (!aiSource.trim()) { toast.error("Enter a website URL or a topic"); return; }
    setAiBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/slides/generate`, { source: aiSource.trim(), count: Number(aiCount) });
      const gen = (r.data.slides || []).map((s) => ({ ...s, bullets: (s.bullets || []).join("\n") }));
      const next = [...slidesRef.current, ...gen];
      setSlides(next);
      // Auto-save so the new cards are persisted immediately (and any old render is marked
      // stale) — otherwise hitting "Render cards reel" would re-render the previous version.
      try {
        await persist(next);
        toast.success(`Added ${gen.length} AI card${gen.length === 1 ? "" : "s"} — saved. Review & edit, then render.`);
      } catch {
        toast.success(`Added ${gen.length} AI card${gen.length === 1 ? "" : "s"} — review, then Save`);
      }
      setAiSource("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI generation failed");
    } finally {
      setAiBusy(false);
    }
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCsvBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/projects/${project.id}/slides/import-csv`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const imported = (r.data.slides || []).map((s) => ({ ...s, bullets: (s.bullets || []).join("\n") }));
      if (imported.length === 0) { toast.error("No usable rows found in that CSV"); return; }
      const next = [...slidesRef.current, ...imported];
      setSlides(next);
      try {
        await persist(next);
        toast.success(`Imported ${imported.length} card${imported.length === 1 ? "" : "s"} — saved. Review & edit, then render.`);
      } catch {
        toast.success(`Imported ${imported.length} card${imported.length === 1 ? "" : "s"} — review, then Save`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "CSV import failed");
    } finally {
      setCsvBusy(false);
      e.target.value = "";
    }
  };

  const downloadTemplate = () => {
    const csv =
      "title,bullets,narration\n" +
      "\"Air Conditioning\",\"Same-day installs & repairs|Licensed & insured|24/7 service\",\"Beat the heat — we install and repair AC the same day, day or night.\"\n" +
      "\"Free Estimates\",\"No-obligation quotes|Upfront pricing|Fast turnaround\",\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "cards-template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const _csvCell = (s) => {
    const v = (s ?? "").toString();
    return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  };
  const _bulletList = (s) => (s.bullets || "").split("\n").map((b) => b.trim()).filter(Boolean);
  const _baseName = () =>
    ((project?.name || project?.settings?.company || "cards") + "").replace(/[^a-z0-9]+/gi, "-").toLowerCase();

  const exportCsv = () => {
    const list = slidesRef.current;
    if (!list.length) { toast.error("No cards to export yet"); return; }
    const rows = [["#", "title", "bullets", "narration"]];
    list.forEach((s, i) => rows.push([String(i + 1), s.title || "", _bulletList(s).join(" | "), s.voiceover || ""]));
    const csv = rows.map((r) => r.map(_csvCell).join(",")).join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${_baseName()}-cards.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("CSV exported — the client can edit it and you re-import to apply");
  };

  const exportPrintable = () => {
    const list = slidesRef.current;
    if (!list.length) { toast.error("No cards to export yet"); return; }
    const esc = (s) => (s ?? "").toString().replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const cards = list.map((s, i) => {
      const b = _bulletList(s);
      return `<section class="card">
        <div class="num">Card ${i + 1}</div>
        <h2>${esc(s.title || "(untitled)")}</h2>
        ${b.length ? `<ul>${b.map((x) => `<li>${esc(x)}</li>`).join("")}</ul>` : ""}
        <div class="nar"><span>Narration</span><p>${esc(s.voiceover || "—")}</p></div>
      </section>`;
    }).join("");
    const title = esc(project?.name || "Cards");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${title} — cards for review</title>
      <style>
        *{box-sizing:border-box} body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#18181b;margin:0;background:#f4f4f5}
        .wrap{max-width:760px;margin:0 auto;padding:48px 32px}
        header{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #f59e0b;padding-bottom:16px;margin-bottom:28px}
        h1{font-size:26px;margin:0} .sub{color:#71717a;font-size:13px}
        .toolbar{margin:0 0 24px} .btn{background:#f59e0b;border:0;color:#0a0500;font-weight:700;padding:10px 18px;border-radius:999px;cursor:pointer;font-size:14px}
        .card{background:#fff;border:1px solid #e4e4e7;border-radius:14px;padding:22px 24px;margin-bottom:16px}
        .num{font-size:11px;letter-spacing:.15em;text-transform:uppercase;color:#f59e0b;font-weight:700}
        h2{font-size:20px;margin:6px 0 10px} ul{margin:0 0 14px;padding-left:20px} li{margin:3px 0;color:#3f3f46}
        .nar{border-top:1px dashed #e4e4e7;padding-top:12px} .nar span{display:block;font-size:10px;letter-spacing:.15em;text-transform:uppercase;color:#a1a1aa;font-weight:700;margin-bottom:4px}
        .nar p{margin:0;color:#27272a;line-height:1.5;font-style:italic}
        @media print{body{background:#fff} .toolbar{display:none} .wrap{padding:0} .card{break-inside:avoid;border-color:#d4d4d8}}
      </style></head><body><div class="wrap">
      <header><div><h1>${title}</h1><div class="sub">${list.length} card${list.length === 1 ? "" : "s"} · for client review</div></div><div class="sub">Listing to Video</div></header>
      <div class="toolbar"><button class="btn" onclick="window.print()">Print / Save as PDF</button></div>
      ${cards}</div></body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
    else {
      const blob = new Blob([html], { type: "text/html" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${_baseName()}-cards.html`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast("Opened as a download — open it and use Print → Save as PDF");
    }
  };

  const narrateOne = async (i) => {
    const s = slidesRef.current[i];
    if (!s) return;
    const bullets = (s.bullets || "").split("\n").map((b) => b.trim()).filter(Boolean);
    if (!s.title?.trim() && bullets.length === 0) {
      toast.error("Add a headline or bullet points first");
      return;
    }
    setNarrateBusy(i);
    try {
      const r = await api.post(`/projects/${project.id}/slides/narrate`, { title: s.title || "", bullets });
      update(i, { voiceover: r.data.narration, narrate: true });
      toast.success("Narration written — Save to keep it");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't write narration");
    } finally {
      setNarrateBusy(-1);
    }
  };

  const narrateAll = async () => {
    const list = slidesRef.current;
    const targets = list
      .map((s, i) => ({
        i,
        blank: !(s.voiceover || "").trim(),
        title: s.title || "",
        bullets: (s.bullets || "").split("\n").map((b) => b.trim()).filter(Boolean),
      }))
      .filter((t) => t.blank && (t.title.trim() || t.bullets.length));
    if (targets.length === 0) {
      toast("Every card already has narration — use “Rewrite with AI” on a card to change one.");
      return;
    }
    setNarrateAllBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/slides/narrate-batch`, {
        cards: targets.map((t) => ({ title: t.title, bullets: t.bullets })),
      });
      const nar = r.data.narrations || [];
      setSlides((cur) =>
        cur.map((s, idx) => {
          const pos = targets.findIndex((t) => t.i === idx);
          if (pos >= 0 && nar[pos]) return { ...s, voiceover: nar[pos], narrate: true };
          return s;
        })
      );
      const wrote = nar.filter(Boolean).length;
      toast.success(`Wrote narration for ${wrote} card${wrote === 1 ? "" : "s"} — Save to keep it`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't write narration");
    } finally {
      setNarrateAllBusy(false);
    }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("files", f);
      const up = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoMediaId(up.data.uploaded[0].media_id);
    } catch {
      toast.error("Logo upload failed");
    } finally {
      setLogoBusy(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    if (!isCards) return;
    api.get("/projects/voices").then((r) => {
      const vs = r.data.voices || [];
      setVoices(vs);
      const keys = new Set(vs.map((v) => v.key));
      const stored = project.settings?.voice_name;
      setVoiceName(keys.has(stored) ? stored : (r.data.default || vs[0]?.key || ""));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewVoice = async () => {
    if (!voiceName) return;
    setPreviewingVoice(true);
    try {
      const res = await api.post("/projects/voices/preview",
        { voice: voiceName, text: "Here's what makes this place special. [pause] Take a look.", speed: Number(voiceSpeed) },
        { responseType: "blob" });
      new Audio(URL.createObjectURL(res.data)).play();
    } catch {
      toast.error("Preview failed");
    } finally {
      setPreviewingVoice(false);
    }
  };

  const stopPlayback = () => {
    playAllRef.current = false;
    setPlayAll(false);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  };

  const _synthAndPlay = async (text) => {
    const r = await api.post(`/projects/${project.id}/slides/preview-narration`,
      { text, voice: voiceName || undefined, speed: Number(voiceSpeed) });
    const a = new Audio(mediaUrl(r.data.media_id));
    audioRef.current = a;
    await new Promise((resolve, reject) => {
      a.onended = resolve;
      a.onerror = () => reject(new Error("playback failed"));
      a.play().catch(reject);
    });
  };

  const listenCard = async (i) => {
    const s = slidesRef.current[i];
    const text = (s?.voiceover || "").trim();
    if (!text) { toast.error("This card has no voiceover yet — write one first"); return; }
    stopPlayback();
    setListenBusy(i);
    try {
      await _synthAndPlay(text);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't play narration");
    } finally {
      setListenBusy(-1);
    }
  };

  const playAllInOrder = async () => {
    if (playAllRef.current) { stopPlayback(); return; }
    const targets = slidesRef.current
      .map((s, i) => ({ i, text: (s.voiceover || "").trim(), on: s.narrate }))
      .filter((t) => t.on && t.text);
    if (targets.length === 0) { toast.error("No narrated cards yet — write voiceovers first"); return; }
    stopPlayback();
    playAllRef.current = true;
    setPlayAll(true);
    try {
      for (const t of targets) {
        if (!playAllRef.current) break;
        setListenBusy(t.i);
        await _synthAndPlay(t.text);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Playback stopped");
    } finally {
      setListenBusy(-1);
      playAllRef.current = false;
      setPlayAll(false);
      audioRef.current = null;
    }
  };


  const saveVoice = async () => {
    if (!voiceName) return;
    setSavingVoice(true);
    try {
      await api.post(`/projects/${project.id}/revoice`, { voice_name: voiceName, speed: Number(voiceSpeed) });
      toast.success("Narration voice saved — it'll be used on your next render");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save voice");
    } finally {
      setSavingVoice(false);
    }
  };

  const saveBranding = async () => {
    setSavingBrand(true);
    try {
      await api.post(`/projects/${project.id}/cards/branding`, {
        bg_color: brandColor,
        intro_enabled: introEnabled,
        company_name: companyName,
        details: companyDetails,
        logo_media_id: logoMediaId,
      });
      toast.success("Branding saved — it applies on the next render");
      onChange?.();  // refresh the parent's project so a tab-switch/remount doesn't repopulate old values
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save branding");
    } finally {
      setSavingBrand(false);
    }
  };

  const genBg = async (i) => {
    const s = slidesRef.current[i];
    if (!s) return;
    const bullets = (s.bullets || "").split("\n").map((b) => b.trim()).filter(Boolean);
    if (!s.title?.trim() && bullets.length === 0) {
      toast.error("Add a headline or bullet points first");
      return;
    }
    setBgAiBusy(i);
    try {
      const r = await api.post(`/projects/${project.id}/slides/bg/generate`, { title: s.title || "", bullets });
      const jobId = r.data.job_id;
      let mediaId = null;
      for (let n = 0; n < 22; n++) {
        await new Promise((res) => setTimeout(res, 4000));
        const st = await api.get(`/projects/${project.id}/slides/bg/${jobId}`);
        if (st.data.status === "ready" && st.data.media_id) { mediaId = st.data.media_id; break; }
        if (st.data.status === "failed") throw new Error(st.data.error || "generation failed");
      }
      if (!mediaId) throw new Error("timed out — try again");
      update(i, { background_media_id: mediaId, background_kind: "image" });
      toast.success("AI background added — Save to keep it");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "AI background failed");
    } finally {
      setBgAiBusy(-1);
    }
  };

  const uploadBg = async (i, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isVideo = (f.type || "").startsWith("video");
    if (isVideo && f.size > 50 * 1024 * 1024) {
      toast.error("That video is over 50MB — please use a shorter/smaller clip for a card background");
      e.target.value = "";
      return;
    }
    setBgBusy(i);
    try {
      const fd = new FormData();
      fd.append("files", f);
      const up = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update(i, { background_media_id: up.data.uploaded[0].media_id, background_kind: isVideo ? "video" : "image" });
    } catch (err) {
      toast.error("Background upload failed");
    } finally {
      setBgBusy(-1);
      e.target.value = "";
    }
  };

  const persist = async (list) => {
    const src = list || slidesRef.current;
    const payload = {
      slides: src.map((s) => ({
        id: s.id,
        title: s.title,
        bullets: (s.bullets || "").split("\n").map((b) => b.trim()).filter(Boolean),
        duration: Number(s.duration) || 4,
        motion: s.motion,
        narrate: !!s.narrate,
        voiceover: s.voiceover || "",
        position: Number(s.position),
        background_media_id: s.background_media_id || null,
        background_kind: s.background_kind || null,
        bg_start: Number(s.bg_start) || 0,
        bg_mute: s.bg_mute !== false,
      })),
    };
    const r = await api.put(`/projects/${project.id}/slides`, payload);
    return (r.data.slides || []).map((s) => ({ ...s, bullets: (s.bullets || []).join("\n") }));
  };

  // Expose the saver so the parent's "Render cards reel" button can persist the current
  // cards BEFORE running the render (prevents rendering a stale/previous version).
  useEffect(() => {
    if (onRegisterSave) onRegisterSave(async () => { await persist(); });
    return () => { if (onRegisterSave) onRegisterSave(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const saved = await persist();
      setSlides(saved);
      toast.success(isCards ? "Cards saved — hit Render cards reel" : "Info slides saved — they'll appear next time you render");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save slides");
    } finally {
      setSaving(false);
    }
  };

  const posOptions = () => {
    const opts = [{ v: 0, label: "At the very start" }];
    let sceneNo = 0;
    scenes.forEach((s, i) => {
      let label;
      if (s.kind === "agent") {
        label = "After the agent intro";
      } else {
        sceneNo += 1;
        label = `After scene ${sceneNo}${s.caption ? ` — ${s.caption}` : ""}`;
      }
      opts.push({ v: i + 1, label });
    });
    opts.push({ v: 9999, label: "At the very end (before outro)" });
    return opts;
  };

  if (loading) return <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading slides…</div>;

  return (
    <div className="space-y-6" data-testid="info-slides-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-zinc-100 font-semibold"><Layers className="w-4 h-4 text-amber-500" /> {isCards ? "Your cards" : "Info slides"}</h3>
          <p className="mt-1 text-xs text-zinc-500 max-w-xl">
            {isCards
              ? "Build your reel from these cards — headline + bullet points, spoken aloud. They play top-to-bottom in the order listed. Each card stays on screen as long as its voiceover (or your set duration, whichever is longer)."
              : "Add static text cards (headline + bullet points) anywhere in the reel — perfect for services, credentials or offers. No AI generation needed. Optionally have a voice read them out."}
          </p>
          {!isCards && scenes.length === 0 && (
            <p className="mt-2 text-[11px] text-amber-500/80 max-w-xl" data-testid="slides-noscenes-hint">
              Tip: write your script (Script tab) or run the reel first — then you can drop a card <span className="underline">after any specific scene</span>, not just start/end.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {slides.length > 0 && (
            <button data-testid="narrate-all" onClick={narrateAll} disabled={narrateAllBusy}
              title="AI-write the voiceover for every card that doesn't have one yet"
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 disabled:opacity-60 whitespace-nowrap">
              {narrateAllBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
              {narrateAllBusy ? "Writing…" : "Narrate all"}
            </button>
          )}
          {slides.length > 0 && (
            <button data-testid="preview-narration-all" onClick={playAllInOrder}
              title="Listen to every card's voiceover back-to-back, in order — before you render"
              className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border disabled:opacity-60 whitespace-nowrap ${playAll ? "border-red-500/60 text-red-400 hover:bg-red-500/10" : "border-amber-500/50 text-amber-400 hover:bg-amber-500/10"}`}>
              {playAll ? <Square className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
              {playAll ? "Stop" : "Preview in order"}
            </button>
          )}
          <button data-testid="add-slide" onClick={add} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 whitespace-nowrap">
            <Plus className="w-3.5 h-3.5" /> {isCards ? "Add card" : "Add slide"}
          </button>
        </div>
      </div>

      {slides.length === 0 && (
        <div className="text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-sm p-6 text-center">
          {isCards ? "No cards yet." : "No info slides yet."} Tap <span className="text-amber-400">{isCards ? "Add card" : "Add slide"}</span> to create one.
        </div>
      )}

      {isCards && (
        <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-4 space-y-4" data-testid="branding-box">
          <div className="flex items-center gap-2 text-amber-400 text-xs font-medium">
            <Layers className="w-3.5 h-3.5" /> Branding (applies to the whole reel)
          </div>

          <div className="space-y-2">
            <span className="text-xs text-zinc-400">Card background colour</span>
            <div className="flex flex-wrap items-center gap-2">
              <button data-testid="brand-color-black" onClick={() => setBrandColor("#0A0A0C")}
                className={`w-8 h-8 rounded-sm border ${brandColor.toLowerCase() === "#0a0a0c" ? "border-amber-500" : "border-zinc-700"}`}
                style={{ background: "#0A0A0C" }} title="Black" />
              <button data-testid="brand-color-white" onClick={() => setBrandColor("#FFFFFF")}
                className={`w-8 h-8 rounded-sm border ${brandColor.toLowerCase() === "#ffffff" ? "border-amber-500" : "border-zinc-700"}`}
                style={{ background: "#FFFFFF" }} title="White" />
              <input data-testid="brand-color-picker" type="color" value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                className="w-8 h-8 bg-transparent border border-zinc-700 rounded-sm cursor-pointer" title="Custom colour" />
              <input data-testid="brand-color-hex" value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                placeholder="#1E3A8A"
                className="w-28 bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-sm text-zinc-100 focus:border-amber-500/60 outline-none" />
              <span className="text-[11px] text-zinc-500">Paste your client's brand colour to match their site</span>
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
            <input data-testid="brand-intro-toggle" type="checkbox" checked={introEnabled}
              onChange={(e) => setIntroEnabled(e.target.checked)} className="accent-amber-500" />
            Show a branded intro card first (logo + company name + details)
          </label>

          {introEnabled && (
            <div className="space-y-3 pl-1">
              <div>
                <input data-testid="brand-company-name" value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder={logoMediaId ? "Company name (optional — leave blank when using a logo)" : "Company name (e.g. Alpha HVAC)"}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:border-amber-500/60 outline-none" />
                {logoMediaId && (
                  <p className="mt-1 text-[11px] text-zinc-500">Using a logo? Leave this blank so the name isn't shown twice. The intro will show just your logo + the details line.</p>
                )}
              </div>
              <input data-testid="brand-company-details" value={companyDetails}
                onChange={(e) => setCompanyDetails(e.target.value)}
                placeholder="One line of details (e.g. Licensed HVAC • Since 2004)"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:border-amber-500/60 outline-none" />
              <div className="flex items-center gap-3">
                {logoMediaId && (
                  <div className="relative">
                    <img src={mediaUrl(logoMediaId)} alt="logo" className="w-16 h-16 object-contain rounded-sm border border-zinc-700 bg-zinc-800/60 p-1" />
                    <button data-testid="brand-logo-remove" onClick={() => setLogoMediaId(null)}
                      className="absolute -top-2 -right-2 bg-zinc-900 border border-zinc-700 rounded-full p-0.5 text-zinc-400 hover:text-red-400"><X className="w-3 h-3" /></button>
                  </div>
                )}
                <label data-testid="brand-logo-upload" className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-zinc-700 text-zinc-300 hover:border-amber-500/60 cursor-pointer">
                  {logoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                  {logoMediaId ? "Change logo" : "Upload logo"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} disabled={logoBusy} />
                </label>
              </div>
            </div>
          )}

          <button data-testid="save-branding" onClick={saveBranding} disabled={savingBrand}
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-sm bg-amber-500 text-zinc-950 font-medium hover:bg-amber-400 disabled:opacity-60">
            {savingBrand ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save branding
          </button>
        </div>
      )}

      {isCards && (
        <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-4 space-y-3" data-testid="voice-box">
          <div className="flex items-center gap-2 text-amber-400 text-xs font-medium">
            <Mic className="w-3.5 h-3.5" /> Narration voice (ElevenLabs)
          </div>
          <p className="text-[11px] text-zinc-500">Used for every card that has narration turned on. Pick one, preview it, then Save.</p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              data-testid="voice-select"
              value={voiceName}
              onChange={(e) => setVoiceName(e.target.value)}
              className="flex-1 min-w-[200px] bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:border-amber-500/60 outline-none"
            >
              {voices.length === 0 && <option value="">Loading voices…</option>}
              {voices.map((v) => (
                <option key={v.key} value={v.key}>{v.name || v.key}</option>
              ))}
            </select>
            <button data-testid="voice-preview" onClick={previewVoice} disabled={previewingVoice || !voiceName}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-zinc-700 text-zinc-300 hover:border-amber-500/60 disabled:opacity-60">
              {previewingVoice ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Preview
            </button>
            <button data-testid="voice-save" onClick={saveVoice} disabled={savingVoice || !voiceName}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-sm bg-amber-500 text-zinc-950 font-medium hover:bg-amber-400 disabled:opacity-60">
              {savingVoice ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save voice
            </button>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <span className="text-xs text-zinc-400 whitespace-nowrap">Speed</span>
            <input data-testid="voice-speed" type="range" min="0.7" max="1.2" step="0.05"
              value={voiceSpeed} onChange={(e) => setVoiceSpeed(e.target.value)}
              className="flex-1 accent-amber-500" />
            <span className="text-xs text-zinc-300 w-20 text-right">
              {Number(voiceSpeed) < 0.95 ? "Slower" : Number(voiceSpeed) > 1.05 ? "Faster" : "Normal"} ({Number(voiceSpeed).toFixed(2)}×)
            </span>
          </div>
          <p className="text-[11px] text-zinc-500">
            Slide the speed left to slow the narration down. To add a <b>pause</b>, type
            <code className="mx-1 px-1 bg-zinc-800 rounded text-zinc-300">[pause]</code> in a card's narration
            (1 second), or <code className="mx-1 px-1 bg-zinc-800 rounded text-zinc-300">[pause 1.5s]</code> for a custom length (max 3s).
          </p>
        </div>
      )}

      <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-4 space-y-3" data-testid="ai-fill-box">
        <div className="flex items-center gap-2 text-amber-400 text-xs font-medium">
          <Wand2 className="w-3.5 h-3.5" /> Generate cards with AI
        </div>
        <p className="text-[11px] text-zinc-500">Paste a <b>website</b> URL, or just <b>describe the business/topic</b> — perfect if they only have social media. AI drafts the headline, bullets and voiceover for each card. <span className="text-zinc-600">(Facebook/Instagram links can't be read — describe the business instead.)</span></p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="ai-source-input"
            value={aiSource}
            onChange={(e) => setAiSource(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !aiBusy && generate()}
            placeholder="https://yourbusiness.com   —or—   'Riverside Anglers — Manitoba fishing group, local spots & gear tips'"
            className="flex-1 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:border-amber-500/60 outline-none"
          />
          <select data-testid="ai-count" value={aiCount} onChange={(e) => setAiCount(Number(e.target.value))} className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-2 text-sm text-zinc-100">
            {[3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n} cards</option>)}
          </select>
          <button data-testid="ai-generate" onClick={generate} disabled={aiBusy} className="flex items-center justify-center gap-1.5 text-sm px-4 py-2 rounded-sm bg-amber-500 text-zinc-950 font-medium hover:bg-amber-400 disabled:opacity-60 whitespace-nowrap">
            {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {aiBusy ? "Drafting…" : "Generate"}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-3 border-t border-zinc-800 pt-3">
          <FileSpreadsheet className="w-3.5 h-3.5 text-amber-400 shrink-0" />
          <span className="text-[11px] text-zinc-500 flex-1 min-w-[180px]">
            Have a list already? Import a <b>CSV</b> — columns <code className="px-1 bg-zinc-800 rounded text-zinc-300">title, bullets, narration</code> (bullets separated by <code className="px-1 bg-zinc-800 rounded text-zinc-300">|</code>). Blank narration is auto-written by AI. Cards are added to your list.
          </span>
          <button data-testid="csv-template" onClick={downloadTemplate} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-zinc-700 text-zinc-300 hover:border-amber-500/60 whitespace-nowrap">
            <Download className="w-3.5 h-3.5" /> Template
          </button>
          <label data-testid="csv-import" className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 cursor-pointer whitespace-nowrap ${csvBusy ? "opacity-60 pointer-events-none" : ""}`}>
            {csvBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {csvBusy ? "Importing…" : "Import CSV"}
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={importCsv} disabled={csvBusy} data-testid="csv-import-input" />
          </label>
          <div className="mx-1 h-6 w-px bg-zinc-800" />
          <button data-testid="csv-export" onClick={exportCsv} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-zinc-700 text-zinc-300 hover:border-amber-500/60 whitespace-nowrap" title="Download all cards as a CSV your client can edit and you can re-import">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button data-testid="cards-print" onClick={exportPrintable} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-zinc-700 text-zinc-300 hover:border-amber-500/60 whitespace-nowrap" title="Open a clean, printable review copy (Save as PDF)">
            <FileText className="w-3.5 h-3.5" /> Review copy
          </button>
        </div>
      </div>


      <div className="space-y-4">
        {slides.map((s, i) => (
          <div
            key={i}
            data-testid={`slide-card-${i}`}
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); reorder(dragIdx, i); setDragIdx(null); }}
            className={`border rounded-sm p-4 bg-zinc-900/40 space-y-3 transition-colors ${dragIdx === i ? "border-amber-500/70 opacity-60" : "border-zinc-800"}`}
          >
            <div className="flex items-center justify-between">
              <span
                data-testid={`slide-drag-${i}`}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragEnd={() => setDragIdx(null)}
                className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-500 cursor-grab active:cursor-grabbing select-none"
                title="Drag to reorder"
              >
                <GripVertical className="w-3.5 h-3.5" /> {isCards ? "Card" : "Slide"} {i + 1}
              </span>
              <button data-testid={`remove-slide-${i}`} onClick={() => remove(i)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>

            <input
              data-testid={`slide-title-${i}`}
              value={s.title}
              onChange={(e) => update(i, { title: e.target.value })}
              placeholder="Headline (e.g. Air Conditioning)"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:border-amber-500/60 outline-none"
            />
            <textarea
              data-testid={`slide-bullets-${i}`}
              value={s.bullets}
              onChange={(e) => update(i, { bullets: e.target.value })}
              placeholder={"One bullet per line\nSame-day installs & repairs\nLicensed & insured"}
              rows={4}
              className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:border-amber-500/60 outline-none resize-y"
            />

            <div className={`grid grid-cols-1 ${isCards ? "sm:grid-cols-2" : "sm:grid-cols-3"} gap-3`}>
              {!isCards && (
                <label className="text-xs text-zinc-400 space-y-1">
                  <span>Position</span>
                  <select data-testid={`slide-position-${i}`} value={s.position} onChange={(e) => update(i, { position: Number(e.target.value) })} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-sm text-zinc-100">
                    {posOptions().map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </select>
                </label>
              )}
              <label className="text-xs text-zinc-400 space-y-1">
                <span>Min duration: {Number(s.duration).toFixed(1)}s <span className="text-zinc-600">(extends to voice)</span></span>
                <input data-testid={`slide-duration-${i}`} type="range" min={2} max={10} step={0.5} value={s.duration} onChange={(e) => update(i, { duration: Number(e.target.value) })} className="w-full accent-amber-500" />
              </label>
              <label className="text-xs text-zinc-400 space-y-1">
                <span>Motion</span>
                <select data-testid={`slide-motion-${i}`} value={s.motion} onChange={(e) => update(i, { motion: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-sm text-zinc-100">
                  <option value="kenburns">Slow zoom</option>
                  <option value="still">Hold still</option>
                </select>
              </label>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {s.background_media_id ? (
                <div className="relative">
                  {s.background_kind === "video" ? (
                    <video src={mediaUrl(s.background_media_id)} muted loop autoPlay playsInline
                      className="w-24 h-14 object-cover rounded-sm border border-zinc-700" data-testid={`slide-bg-video-${i}`} />
                  ) : (
                    <img src={mediaUrl(s.background_media_id)} alt="" className="w-24 h-14 object-cover rounded-sm border border-zinc-700" />
                  )}
                  <button data-testid={`slide-bg-remove-${i}`} onClick={() => update(i, { background_media_id: null, background_kind: null })} className="absolute -top-2 -right-2 bg-zinc-900 border border-zinc-700 rounded-full p-0.5 text-zinc-400 hover:text-red-400"><X className="w-3 h-3" /></button>
                </div>
              ) : null}
              <label data-testid={`slide-bg-upload-${i}`} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-zinc-700 text-zinc-300 hover:border-amber-500/60 cursor-pointer">
                {bgBusy === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                {s.background_media_id ? "Change background" : "Add background photo / video"}
                <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => uploadBg(i, e)} disabled={bgBusy === i} />
              </label>
              <button
                data-testid={`slide-bg-ai-${i}`}
                onClick={() => genBg(i)}
                disabled={bgAiBusy === i}
                title="Generate a background image that suits this card"
                className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 disabled:opacity-60"
              >
                {bgAiBusy === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {bgAiBusy === i ? "Generating…" : "AI background"}
              </button>
            </div>

            {s.background_kind === "video" && (
              <div className="flex flex-wrap items-center gap-4 border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-950/40" data-testid={`slide-video-opts-${i}`}>
                <label className="flex items-center gap-2 text-xs text-zinc-400">
                  Start at
                  <input data-testid={`slide-bg-start-${i}`} type="number" min={0} step={0.5}
                    value={s.bg_start ?? 0}
                    onChange={(e) => update(i, { bg_start: Math.max(0, Number(e.target.value) || 0) })}
                    className="w-20 bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1 text-sm text-zinc-100 focus:border-amber-500/60 outline-none" />
                  <span className="text-zinc-600">sec into the clip</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                  <input data-testid={`slide-bg-sound-${i}`} type="checkbox"
                    checked={s.bg_mute === false}
                    onChange={(e) => update(i, { bg_mute: !e.target.checked })}
                    className="accent-amber-500" />
                  Use the video's own sound
                </label>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                  <input data-testid={`slide-narrate-${i}`} type="checkbox" checked={s.narrate} onChange={(e) => update(i, { narrate: e.target.checked })} className="accent-amber-500" />
                  Narrate this slide (voiceover)
                </label>
                <div className="flex items-center gap-2">
                  {s.narrate && (s.voiceover || "").trim() && (
                    <button
                      data-testid={`slide-listen-${i}`}
                      onClick={() => (listenBusy === i && !playAll ? stopPlayback() : listenCard(i))}
                      disabled={playAll}
                      title="Listen to this card's voiceover"
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border border-zinc-700 text-zinc-300 hover:border-amber-500/60 disabled:opacity-40"
                    >
                      {listenBusy === i && !playAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
                      Listen
                    </button>
                  )}
                  <button
                    data-testid={`slide-narrate-ai-${i}`}
                    onClick={() => narrateOne(i)}
                    disabled={narrateBusy === i}
                    title="Write the voiceover with AI from this card's title + bullets"
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 disabled:opacity-60"
                  >
                    {narrateBusy === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    {narrateBusy === i ? "Writing…" : (s.voiceover ? "Rewrite with AI" : "Write with AI")}
                  </button>
                </div>
              </div>
              {s.narrate && (
                <textarea
                  data-testid={`slide-voiceover-${i}`}
                  value={s.voiceover}
                  onChange={(e) => update(i, { voiceover: e.target.value })}
                  placeholder="What the voice should say over this slide… or tap “Write with AI”."
                  rows={2}
                  className="mt-2 w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:border-amber-500/60 outline-none resize-y"
                />
              )}
            </div>
          </div>
        ))}
      </div>

      <button data-testid="save-slides" onClick={save} disabled={saving} className="flex items-center gap-2 text-sm px-4 py-2.5 rounded-sm bg-amber-500 text-zinc-950 font-medium hover:bg-amber-400 disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {isCards ? "Save cards" : "Save info slides"}
      </button>
    </div>
  );
}
